// A sortable table heading. Ported from Zanetto Tools (phase 7 stage 2).
//
// The <th> is the button rather than containing one: the whole heading is the
// hit target, which matters on a tablet. `aria-sort` is what makes the state
// audible — the arrow alone tells a screen-reader user nothing.

export type SortDirection = 'asc' | 'desc';

// The resting arrow was gray-300, which is 1.6:1 on the header tint — a hint
// that a column can be sorted is useless if it cannot be seen. gray-600 reads
// as available without competing with the arrow on the column actually sorted.
function SortIcon({ active, dir }: { active: boolean; dir: SortDirection }) {
  if (!active) return <span className="ml-1 text-xs text-gray-600">↕</span>;
  return <span className="ml-1 text-xs font-bold text-brand-800">{dir === 'asc' ? '↑' : '↓'}</span>;
}

export function SortHeader<K extends string>({
  label,
  sortKey,
  current,
  dir,
  onSort,
  className = '',
}: {
  label: string;
  sortKey: K;
  current: K;
  dir: SortDirection;
  onSort: (key: K) => void;
  className?: string;
}) {
  const active = current === sortKey;
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`table-header cursor-pointer select-none transition-colors hover:bg-gray-100 ${
        active ? 'text-gray-900' : ''
      } ${className}`}
      onClick={() => onSort(sortKey)}
    >
      {/* A real button inside, so the heading is reachable and operable by
          keyboard — a click handler on a <th> alone is mouse-only. */}
      <button
        type="button"
        onClick={(event) => {
          // The <th> handler already fires; without this the sort toggles
          // twice and lands back where it started.
          event.stopPropagation();
          onSort(sortKey);
        }}
        className="inline-flex cursor-pointer items-center rounded text-inherit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
      >
        {label}
        <SortIcon active={active} dir={dir} />
      </button>
    </th>
  );
}
